import apiClient from "@/api/apiClient";
import Vue from "vue";

// initial state
const state = {
    facets: [],
    profiles: [],
    profilesLastUpdatedAt: null,
    filters: {
        facetChoices: [],
        searchQuery: null,
        postalCode: {
            code: null,
            distance: 25,
        },
        retrievedPostalCode: {
            code: null,
            lat: null,
            lng: null,
        },
    },
    filteredProfiles: [],
    profilePage: 1,
    loadedProfiles: [],
}

// getters
const getters = {
}

// actions
const actions = {
    getProfile({commit, state}, uuid) {
        // const updateInterval = 15 * 60 * 1000; // 15 min
        const updateInterval = 0; // Op verzoek van Ellen op 0 gezet.
        // Ignore if already loaded  within the interval.
        const loadedProfile = state.loadedProfiles.find(profile => profile.uuid === uuid);
        if (loadedProfile && loadedProfile.loadedAt > (new Date()).getTime() - updateInterval) {
            return loadedProfile;
        }

        return apiClient.get('/profielen/'+uuid)
            .then(response => {
                let profile = response.data;
                profile.loadedAt = (new Date()).getTime();
                commit('LOAD_PROFILE', profile);
                return profile;
            });
    },
    updateProfiles({commit}) {
        return apiClient.get('/profielen')
            .then(response => {
                commit('SET_PROFILES', response.data.profiles);
                commit('SET_FACETS', response.data.facets);
                commit('UPDATE_FILTERED_PROFILES');
            });
    },
    updateFacets({commit}) {
        return apiClient.get('/profielen/facetten')
            .then(response => {
                commit('SET_FACETS', response.data);
                commit('UPDATE_FILTERED_PROFILES');
            });
    },
    updateFacetFilter({commit}, {facet, choices}) {
        commit('SET_FACET_FILTER', {facet, choices});
        commit('UPDATE_FILTERED_PROFILES');
    },
    updateSearchQuery({commit}, searchQuery) {
        commit('SET_SEARCH_QUERY', searchQuery);
        commit('UPDATE_FILTERED_PROFILES');
    },
    updateProfilePage({commit}, profilePage) {
        commit('SET_PROFILE_PAGE', profilePage);
    },
    updatePostalCode({commit, state}, postalCode) {
        const update = postalCode.code || state.filters.postalCode.code;

        commit('SET_POSTAL_CODE', postalCode);

        if (update) {
            if (postalCode.code && postalCode.code.length === 6) {
                return apiClient.get('/geolocation', { params: { address: postalCode.code+', Netherlands' }})
                    .then(response => {
                        commit('SET_RETRIEVED_POSTAL_CODE', {
                            code: postalCode.code,
                            lat: response.data.lat,
                            lng: response.data.lng,
                        });
                        commit('UPDATE_FILTERED_PROFILES');
                    }).catch(() => {
                        commit('SET_RETRIEVED_POSTAL_CODE', {
                            code: null,
                            lat: null,
                            lng: null,
                        });
                        commit('UPDATE_FILTERED_PROFILES');
                    });
            } else {
                commit('SET_RETRIEVED_POSTAL_CODE', {
                    code: null,
                    lat: null,
                    lng: null,
                });
                commit('UPDATE_FILTERED_PROFILES');
            }
        }
    },
}

// mutations
const mutations = {
    LOAD_PROFILE(state, profile) {
        // Remove if already loaded before.
        const existingProfileIndex = state.loadedProfiles.findIndex(p => p.uuid === profile.uuid);
        if (existingProfileIndex !== -1) {
            state.loadedProfiles.splice(existingProfileIndex, 1);
        }

        state.loadedProfiles.push(profile);
    },
    SET_PROFILES(state, profiles) {
        state.profiles = profiles;
        state.profilesLastUpdatedAt = (new Date()).getTime();
    },
    SET_FACETS(state, facets) {
        state.facets = facets;
    },
    UPDATE_FILTERED_PROFILES(state) {
        let profiles = state.profiles;

        if (state.filters.searchQuery) {
            const searchQuery = state.filters.searchQuery.toLowerCase();
            profiles = profiles.filter(profile =>
                profile.name.toLowerCase().includes(searchQuery)
                || profile.summary.toLowerCase().includes(searchQuery)
                || profile.email.toLowerCase().includes(searchQuery)
                || profile.phoneNumber.toLowerCase().includes(searchQuery)
            );
        }

        state.facets.forEach(facet => {
            const facetChoiceIds = state.filters.facetChoices[facet.id];
            if (!facetChoiceIds || facetChoiceIds.length === 0) {
                return;
            }

            profiles = profiles.filter(profile => facetChoiceIds.some(id => !!profile.facetChoices.find(choice => choice.id === id)));
        });

        if (state.filters.retrievedPostalCode.code && state.filters.retrievedPostalCode.lat && state.filters.retrievedPostalCode.lng) {
            profiles = profiles.filter(profile => {
                return state.filters.postalCode.distance >= getDistanceBetweenLocations(state.filters.retrievedPostalCode.lat, state.filters.retrievedPostalCode.lng, profile.latitude, profile.longitude);
            });
        }

        if (state.filteredProfiles.length !== profiles.length) {
            state.profilePage = 1;
        }

        state.filteredProfiles = profiles;
    },
    SET_FACET_FILTER(state, {facet, choices}) {
        Vue.set(state.filters.facetChoices, facet.id, choices);
    },
    SET_SEARCH_QUERY(state, searchQuery) {
        Vue.set(state.filters, 'searchQuery', searchQuery);
    },
    SET_POSTAL_CODE(state, postalCode) {
        Vue.set(state.filters, 'postalCode', postalCode);
    },
    SET_RETRIEVED_POSTAL_CODE(state, retrievedPostalCode) {
        Vue.set(state.filters, 'retrievedPostalCode', retrievedPostalCode);
    },
    SET_PROFILE_PAGE(state, profilePage) {
        state.profilePage = profilePage;
    },
}

export default {
    state,
    getters,
    actions,
    mutations
}

function getDistanceBetweenLocations(lat1, lon1, lat2, lon2) {
    if ((lat1 == lat2) && (lon1 == lon2)) {
        return 0;
    }
    else {
        const radlat1 = Math.PI * lat1/180;
        const radlat2 = Math.PI * lat2/180;
        const theta = lon1-lon2;
        const radtheta = Math.PI * theta/180;
        let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
        if (dist > 1) {
            dist = 1;
        }
        dist = Math.acos(dist);
        dist = dist * 180/Math.PI;
        dist = dist * 60 * 1.1515;
        dist = dist * 1.609344;
        return dist;
    }
}
